/**
 * Client for the `cal-integration` edge function. No component talks to the
 * `cal_integrations` table directly. An agent has at most one connection.
 *
 * The function takes the workspace from the agent — never from the browser,
 * so no body carries one — and checks the caller's role in it (view: any
 * member; connect and change: owner, admin, developer; disconnect: owner,
 * admin). It asks Cal.com whether the key and event type work before it saves
 * them, and keeps the key write-only: a connection comes back with
 * `cal_api_key_hint` instead. Keep in sync with
 * `supabase/functions/cal-integration/` and `utils/cal-integration-rules.ts`.
 */

import { api } from "@/lib/api-client";

const BASE = "/cal-integration";

/** A `cal_integrations` row as the function answers it: the key swapped for its hint. */
export interface CalIntegration {
  id: string;
  agent_id: string;
  workspace_id: string;
  cal_event_type_id: number;
  /** "cal_live_…9f2c": the key's cal_<word>_ prefix, "…", its last 4 characters. */
  cal_api_key_hint: string;
  created_at: string | null;
  updated_at: string | null;
}

export interface CreateCalIntegrationBody {
  agent_id: string;
  /** Write-only. Sent trimmed. */
  cal_api_key: string;
  cal_event_type_id: number;
}

/** A change: at least one field. Leave the key out to keep the stored one. */
export type UpdateCalIntegrationBody = Partial<Omit<CreateCalIntegrationBody, "agent_id">>;

/** The agent's connection, or null when it has none — an answer, not an error. */
export const getCalIntegration = (agentId: string) =>
  api.getItemOrNull<CalIntegration>(`${BASE}/agent/${agentId}`);

/** Cal.com is asked first: a key or event type it refuses is a 422 naming the field. */
export const createCalIntegration = (body: CreateCalIntegrationBody) =>
  api.post<CalIntegration>(BASE, body);

/** Checked with Cal.com too, on the pair that would be stored. */
export const updateCalIntegration = (id: string, body: UpdateCalIntegrationBody) =>
  api.put<CalIntegration>(`${BASE}/${id}`, body);

export const deleteCalIntegration = (id: string) => api.del(`${BASE}/${id}`);
